import {EngagementId, EngagementLock, FilterArgs, PaginationArgs, TprmScopeVisibility} from "platformed-client/types";
import api from "../../../api";
import {param, typedQuery} from "../../typing";

export const engagement = typedQuery(
  ["tprm", "engagement", param<EngagementId>("engagementId")],
  async engagementId => {
    return await api.tprm.engagements.get(engagementId);
  },
);

export const engagements = typedQuery(
  ["tprm", "engagements", param<PaginationArgs>("paginationArgs"), param<FilterArgs>("filterArgs")],
  async (paginationArgs, filterArgs) => {
    return await api.tprm.engagements.search(paginationArgs, filterArgs);
  },
);

export const engagementScopes = typedQuery(
  ["tprm", "engagementScopes", param<EngagementId>("engagementId")],
  async engagementId => {
    return await api.tprm.engagements.listScopes(engagementId);
  },
);

/// Source error counts per scope for a data room, keyed by scope_id.
///
/// Scopes with zero errors are absent from the response — look up with
/// `badgesByScopeId.get(scopeId)?.source_error_count ?? 0`.  Use this
/// query in badge/indicator components rather than reading counts from
/// `TprmScopeMin`, which no longer carries `source_counts`.
export const engagementScopeBadges = typedQuery(
  ["tprm", "engagementScopeBadges", param<EngagementId>("engagementId")],
  async engagementId => {
    return await api.tprm.engagements.listScopeBadges(engagementId);
  },
);

export const engagementDiagrams = typedQuery(
  ["tprm", "engagementDiagrams", param<EngagementId>("engagementId")],
  async engagementId => {
    return await api.tprm.engagements.listDiagrams(engagementId);
  },
);

export const engagementExternalQuestionnaires = typedQuery(
  ["tprm", "engagementExternalQuestionnaires", param<EngagementId>("engagementId")],
  async engagementId => {
    return await api.tprm.engagements.listExternalQuestionnaires(engagementId);
  },
);

export const engagementDeletable = typedQuery(
  ["tprm", "engagementDeletable", param<EngagementId>("engagementId")],
  async engagementId => {
    return await api.tprm.engagements.canDelete(engagementId);
  },
);

export const engagementTasks = typedQuery(["tprm", "engagementTasks", param<EngagementLock>("lock")], async lock => {
  return await api.tprm.engagements.listEngagementTasks(lock);
});

export const engagementSources = typedQuery(
  ["tprm", "engagementSources", param<EngagementId>("engagementId")],
  async engagementId => {
    // Get all scopes for the data room
    const scopes = await api.tprm.engagements.listScopes(engagementId);

    // Get sources for all scopes
    const allSources = await Promise.all(
      scopes.map(scope => api.tprm.scopes.listSources(scope.scope_id, "All" as TprmScopeVisibility)),
    );

    // Flatten the results
    return allSources.flat();
  },
);
